/**
 * Browser-side region detection for the first-run setup modal.
 *
 * Detects:
 *   - timezone   — Intl resolved options
 *   - locale     — navigator.language, narrowed to en | ar
 *   - workweek   — Sun–Thu in GCC + Egypt, Mon–Fri elsewhere
 *   - weekStart  — Sun for GCC/MENA, Mon elsewhere
 *
 * All detections are best-effort. The modal pre-fills these values
 * but lets the user override before saving. We never silently apply
 * them — the user must confirm.
 */

export type DetectedRegion = {
  timezone: string;
  locale: "en" | "ar";
  weekStartDay: number;
  workingDays: number;
};

/**
 * Timezones that conventionally use a Sun–Thu workweek. List is
 * deliberately narrow — many countries shifted to Sat–Wed or
 * Mon–Fri in the last decade. When in doubt the user can tweak.
 */
const SUN_THU_TIMEZONES = new Set([
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Kuwait",
  "Asia/Qatar",
  "Asia/Bahrain",
  "Asia/Muscat",
  "Africa/Cairo",
  "Africa/Khartoum",
]);

/**
 * Timezones whose primary language is Arabic. Drives the default
 * `locale` pick when the browser's `navigator.language` doesn't
 * report ar-* (e.g. the user has English UI on an Arab country
 * phone).
 */
const ARABIC_TIMEZONES = new Set([
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Kuwait",
  "Asia/Qatar",
  "Asia/Bahrain",
  "Asia/Muscat",
  "Asia/Baghdad",
  "Asia/Beirut",
  "Asia/Damascus",
  "Asia/Amman",
  "Asia/Jerusalem",
  "Africa/Cairo",
  "Africa/Tunis",
  "Africa/Algiers",
  "Africa/Casablanca",
  "Africa/Tripoli",
  "Africa/Khartoum",
]);

export function detectRegion(): DetectedRegion {
  const tz =
    (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      } catch {
        return "UTC";
      }
    })();

  const browserLang =
    typeof navigator !== "undefined" ? navigator.language || "" : "";
  const locale: "en" | "ar" =
    browserLang.toLowerCase().startsWith("ar") || ARABIC_TIMEZONES.has(tz)
      ? "ar"
      : "en";

  // workingDays is a 7-bit bitmask, day 0 = Sunday.
  //   Sun–Thu = bits 0..4 = 0b0011111 = 31
  //   Mon–Fri = bits 1..5 = 0b0111110 = 62
  const isSunThu = SUN_THU_TIMEZONES.has(tz);
  const workingDays = isSunThu ? 31 : 62;
  const weekStartDay = isSunThu ? 0 /* Sunday */ : 1 /* Monday */;

  return { timezone: tz, locale, weekStartDay, workingDays };
}

/**
 * Best-effort human label for a workweek bitmask. Used in the
 * confirmation modal so the user sees "Sun – Thu (5 days)" not
 * "workingDays = 31".
 */
export function describeWorkweek(
  workingDays: number,
  locale: "en" | "ar",
): string {
  const daysEn = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const daysAr = ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];
  const names = locale === "ar" ? daysAr : daysEn;
  const active: number[] = [];
  for (let i = 0; i < 7; i++) {
    if ((workingDays >> i) & 1) active.push(i);
  }
  if (active.length === 0) return locale === "ar" ? "بدون أيام" : "No days";
  // Detect a contiguous range and shorten to "Sun – Thu (5)".
  const first = active[0];
  const last = active[active.length - 1];
  const contiguous = active.every((d, i) => d === first + i);
  if (contiguous && active.length >= 3) {
    return locale === "ar"
      ? `${names[first]} – ${names[last]} (${active.length} أيام)`
      : `${names[first]} – ${names[last]} (${active.length} days)`;
  }
  return active.map(d => names[d]).join(", ");
}
